import { ColDef } from 'ag-grid-community';
import { Button, Modal, message } from 'antd';
import { noop } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controlled as CodeMirror } from 'react-codemirror2';

import { ReactComponent as ClipboardIcon } from 'assets/icons/copy-clipboard.svg';
import AgTable from 'components/AgTable';
import { useLayoutContext } from 'pages/LayoutContext';
import { getCodeMirrorOptions } from 'utils/CodeMirrorUtil';
import { tc } from 'utils/i18nUtil';
import { copyStringToClipboard } from 'utils/StringUtil';

import { HStack } from './layout';

import './JsonRendererPopover.scss';

export const JsonRendererPopover = ({
  jsonString: rawJsonString,
  format = 'json',
  title,
  getPopupContainer,
}: {
  jsonString: string;
  format?: 'table' | 'json';
  title?: string;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}) => {
  const [visible, setVisible] = useState(false);

  // Normalize: handle non-string values (e.g. objects passed directly)
  const jsonString =
    rawJsonString == null ? '' : typeof rawJsonString === 'string' ? rawJsonString : JSON.stringify(rawJsonString);

  if (!jsonString) {
    return <span className="input-output-renderer input-output-renderer--empty">—</span>;
  }

  return (
    <>
      <a className="input-output-renderer" onClick={() => setVisible(true)}>
        {tc('view')}
      </a>
      <Modal
        title={title}
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        destroyOnClose
        width={520}
        bodyStyle={{ padding: 16 }}
        getContainer={getPopupContainer ? ((getPopupContainer as unknown) as () => HTMLElement) : undefined}>
        {format === 'table' ? (
          <TableRenderer jsonString={jsonString} />
        ) : (
          <CodeMirrorRenderer jsonString={jsonString} />
        )}
        <HStack justify="end" className="json-renderer-modal-footer">
          <Button onClick={() => copyToClipboard(jsonString)} size="small">
            <HStack align="center" spacing="xxxs">
              <ClipboardIcon width={16} height={16} /> <span>{tc('copy')}</span>
            </HStack>
          </Button>
        </HStack>
      </Modal>
    </>
  );
};

interface RendererProps {
  jsonString: string;
}

const TableRenderer = ({ jsonString }: RendererProps) => {
  let records: {
    header: string;
    value: any;
  }[] = [];
  try {
    const json = JSON.parse(jsonString);
    records = Object.keys(json).map((key) => {
      const rawValue = json[key];
      return {
        header: key,
        value: typeof rawValue === 'object' ? JSON.stringify(rawValue) : rawValue,
      };
    });
  } catch (err) {
    // If not valid JSON, show the raw string via copy button only
    console.log('error', err);
  }

  const colDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: tc('header'),
        colId: 'header',
        field: 'header',
      },
      {
        headerName: tc('value'),
        colId: 'value',
        field: 'value',
      },
    ];
  }, []);

  return (
    <div className="popover-table-renderer">
      <AgTable
        suppressCellSelection
        className={'custom-synapse__table'}
        domLayout="autoHeight"
        columnDefs={colDefs}
        rowData={records}
      />
    </div>
  );
};

const CodeMirrorRenderer = ({ jsonString }: RendererProps) => {
  const [editor, setEditor] = useState<CodeMirror.Editor | null>(null);
  const layout = useLayoutContext();

  const updateSize = useCallback((editor?: CodeMirror.Editor) => {
    editor?.setSize('100%', 300);
  }, []);

  useEffect(() => {
    if (editor) {
      updateSize(editor);
      setTimeout(() => editor.refresh(), 0);
    }
  }, [editor, layout.dimensions.content.height, updateSize]);

  let formatedJsonString = jsonString;
  try {
    formatedJsonString = JSON.stringify(JSON.parse(jsonString), undefined, 4);
  } catch (err) {
    // If not valid JSON, show the raw string as-is
  }

  return (
    <CodeMirror
      className="code-mirror-container"
      value={formatedJsonString}
      options={getCodeMirrorOptions(true)}
      editorDidMount={(editor: CodeMirror.Editor) => {
        setEditor(editor);
        updateSize(editor);
        setTimeout(() => editor.refresh(), 0);
      }}
      onBeforeChange={noop}
    />
  );
};

const copyToClipboard = async (textToCopy: string) => {
  let formattedText = textToCopy;
  try {
    formattedText = JSON.stringify(JSON.parse(textToCopy), undefined, 4);
  } catch (err) {
    // copy raw string
  }
  try {
    await copyStringToClipboard(formattedText);
    message.success(tc('copy_to_clipboard_success'));
  } catch (err) {
    message.error(tc('copy_to_clipboard_failure'));
  }
};
